import React from 'react';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { AccountData } from '../../api';
import type { RedeemableOnItemData } from '../../api/models/RedeemableOnItemData';
import type { MultipleTicketsUIObjectData } from '../../api/models/MultipleTicketsUIObjectData';
import { useLanguage } from '../../utils/GeneralUtils';

type RedeemableExperiencesListProps = {
  redeemableOn: RedeemableOnItemData[] | undefined;
  account: AccountData;
};

export const RedeemableExperiencesList = ({ redeemableOn, account }: RedeemableExperiencesListProps) => {
  const { t } = useTranslation();
  const language = useLanguage();

  if (!redeemableOn || redeemableOn.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        {t('manage_passes.pass.redeemableOnAll')}
      </Typography>
    );
  }

  const entries = redeemableOn.map((item) => {
    let name = item.order_page_id;
    let ticketLabel: string | null = null;
    for (const exp of account.experiences || []) {
      const orderPage = exp.order_pages?.find((op) => op.id === item.order_page_id);
      if (orderPage) {
        name = (orderPage.label as Record<string, string>)?.[language] || orderPage.label?.en || item.order_page_id;
        if (item.ticket_type_id) {
          const multiTickets = orderPage.objects?.find(
            (obj): obj is MultipleTicketsUIObjectData => obj.type === 'multipleTickets',
          );
          const ticket = multiTickets?.tickets.find((tk) => tk.id === item.ticket_type_id && tk.archived !== true);
          if (ticket) {
            ticketLabel = (ticket.label as Record<string, string>)?.[language] || ticket.label?.en || null;
          }
        }
        break;
      }
    }
    return { name, limit: item.limit, ticketLabel, allowGuest: item.allow_guest === true };
  });

  return (
    <Grid container flexDirection="column" gap={0.5}>
      <Typography variant="body2" fontWeight={600}>
        {t('manage_passes.pass.redeemableOn')}
      </Typography>
      {entries.map(({ name, limit, ticketLabel, allowGuest }, index) => (
        <Typography key={index} variant="body2" color="text.secondary">
          • {name}
          {ticketLabel && t('manage_passes.pass.ticketTypeSuffix', { ticket: ticketLabel })}
          {limit != null && (
            <Typography component="span" variant="body2" color="text.disabled">
              {' '}
              {t('pass.punch_limit', { count: limit })}
            </Typography>
          )}
          {allowGuest && (
            <Typography component="span" variant="body2" color="text.disabled">
              {' · '}
              {t('manage_passes.pass.allowsGuest')}
            </Typography>
          )}
        </Typography>
      ))}
    </Grid>
  );
};
